import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  UrlSerializer,
  withNavigationErrorHandler,
} from '@angular/router';
import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import {
  currentSubpathEventLabel,
  pathEventLabel,
  routerBasePath,
} from 'src/app/@utils/base-path';
import { SubpathUrlSerializer } from 'src/app/@utils/subpath-url-serializer';
import {
  installChunkReloadGuard,
  reloadOnceForChunkError,
} from 'src/app/@utils/chunk-reload';
import { appEventConfigInitializer } from 'src/app/@configs/app-event-config.init';
import { appIconsInitializer } from 'src/app/@configs/app-icons.init';
import { appMixpanelInitializer } from 'src/app/@configs/app-mixpanel.init';
import { appRouteTrackingInitializer } from 'src/app/@configs/app-route-tracking.init';
import { appScrollTrackingInitializer } from 'src/app/@configs/app-scroll-tracking.init';
import { provideTranslocoConfig } from 'src/app/@configs/transloco.config';
import { authInterceptor } from 'src/app/@interceptors/auth.interceptor';
import { routes } from './app.routes';

// sessionStorage may throw when storage is disabled.
const safeSession = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withNavigationErrorHandler((e) =>
        reloadOnceForChunkError(e.error, safeSession(), () =>
          window.location.reload()
        )
      )
    ),
    {
      provide: UrlSerializer,
      useFactory: () => {
        const loc = inject(DOCUMENT).location;
        return new SubpathUrlSerializer(
          () => currentSubpathEventLabel(loc),
          () => pathEventLabel(loc.pathname)
        );
      },
    },
    // Files load from <base href> ('/event/'); the router base also holds a path event id.
    {
      provide: APP_BASE_HREF,
      useFactory: () => routerBasePath(inject(DOCUMENT).location.pathname),
    },
    provideAppInitializer(() => installChunkReloadGuard()),
    importProvidersFrom(
      BrowserModule,
      MatIconModule,
      FormsModule,
      MatSnackBarModule
    ),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideAnimations(),
    provideAppInitializer(() => {
      const initializerFn = appEventConfigInitializer();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = appIconsInitializer();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = appMixpanelInitializer();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = appRouteTrackingInitializer();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = appScrollTrackingInitializer();
      return initializerFn();
    }),
    provideAnimationsAsync(),
    provideTranslocoConfig(),
  ],
};
